import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Msdyn365 from '@msdyn365-commerce/core';
import ConfirmationService, { ConfirmEvent } from './ConfirmationService';

interface IConfirmModalResources {
    yesButtonLabel: string;
    noButtonLabel: string;
}

const DefaultResources: { [locale: string]: IConfirmModalResources } = {
    'en-US': {
        yesButtonLabel: 'OK',
        noButtonLabel: 'Cancel'
    },
    'nb-NO': {
        yesButtonLabel: 'OK',
        noButtonLabel: 'Avbryt'
    }
};

function getResources(locale: string) {
    if (DefaultResources[locale]) {
        return DefaultResources[locale];
    } else {
        return DefaultResources['en-US'];
    }
}

function getFocusable(container: Element | null): HTMLElement[] {
    if (!container) return [];

    return Array.from(
        container.querySelectorAll<HTMLElement>('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])')
    ).filter(el => !el.hasAttribute('disabled'));
}

const useTrapFocus = (target: React.RefObject<HTMLElement>, isActive: boolean) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive || e.key !== 'Tab') return;

            const focusables = getFocusable(target.current);
            if (focusables.length === 0) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [target, isActive]);
};

const ConfirmModal = () => {
    const modalContentRef = React.useRef<HTMLDivElement>(null);
    const confirmButtonRef = React.useRef<HTMLButtonElement>(null);
    const previousActiveElementRef = React.useRef<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [resolveCallback, setResolveCallback] = useState<((confirmed: boolean) => void) | null>(null);

    useTrapFocus(modalContentRef, isOpen);

    const handleConfirm = () => {
        if (resolveCallback) {
            resolveCallback(true);
        }
        setIsOpen(false);
    };

    const handleCancel = () => {
        if (resolveCallback) {
            resolveCallback(false);
        }
        setIsOpen(false);
    };

    useEffect(() => {
        const handleConfirm = ({ title, message, resolve }: ConfirmEvent) => {
            setTitle(title);
            setMessage(message);
            setIsOpen(true);
            setResolveCallback(() => resolve);
        };

        ConfirmationService.onConfirm(handleConfirm);
        return () => ConfirmationService.offConfirm(handleConfirm);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isOpen) {
                if (event.key === 'Escape') {
                    handleCancel();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleCancel]);

    useEffect(() => {
        if (isOpen) {
            previousActiveElementRef.current = document.activeElement as HTMLElement;
            document.body.style.setProperty('--confirm-modal-top', `-${document.documentElement.scrollTop}px`);
            document.body.classList.add('confirm-modal-open');
            // Focus the modal content when it opens
            setTimeout(() => {
                // @ts-ignore - Argument of type '{ focusVisible: boolean; }' is not assignable to parameter of type 'FocusOptions'.
                confirmButtonRef.current?.focus({ focusVisible: false });
            }, 0);
        } else {
            document.body.classList.remove('confirm-modal-open');
            window.scrollTo(0, Math.abs(parseInt(document.body.style.getPropertyValue('--confirm-modal-top') || '0', 10)));
            setTimeout(() => {
                // @ts-ignore - Argument of type '{ focusVisible: boolean; }' is not assignable to parameter of type 'FocusOptions'.
                previousActiveElementRef.current?.focus({ focusVisible: false });
                previousActiveElementRef.current = null;
            }, 0);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const baseClassName = 'confirm-modal';

    let locale = 'en-US';

    if (Msdyn365.isBrowser) {
        if ((window as any).___initialData___?.requestContext?.locale) {
            locale = (window as any).___initialData___.requestContext.locale;
        }
    }

    const resources = getResources(locale);

    return ReactDOM.createPortal(
        <div className={baseClassName}>
            <div className={`${baseClassName}__content`} tabIndex={-1} role='dialog' aria-modal='true' ref={modalContentRef}>
                <h2 className={`${baseClassName}__heading`}>
                    {title}
                </h2>
                <p className={`${baseClassName}__text`} dangerouslySetInnerHTML={{ __html: message }} />
                <div className={`${baseClassName}__button-container`}>
                    <button className={`${baseClassName}__button__no`} onClick={handleCancel}>
                        {resources.noButtonLabel}
                    </button>
                    <button className={`${baseClassName}__button__yes`} onClick={handleConfirm} ref={confirmButtonRef}>
                        {resources.yesButtonLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmModal;
